import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SiteLayout } from '../components/layout/SiteLayout';
import { getTracker } from '../lib/tracking';
import { PageSeo } from '../components/seo/PageSeo';
import { StructuredData } from '../components/seo/StructuredData';
import { useCollectionEntry } from '../hooks/useCollectionEntry';
import type { FieldDefinition } from '../types/cms';
import { EntryContextProvider } from '../context/EntryContext';
import { TemplateBlockRenderer } from '../components/TemplateBlockRenderer';

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

function FieldValue({ field, value }: { field: FieldDefinition; value: unknown }) {
  if (value === undefined || value === null || value === '') return null;

  switch (field.type) {
    case 'boolean':
      if (!value) return null;
      return (
        <div className="flex items-center gap-2 text-sm text-zinc-700">
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <span>{field.label}</span>
        </div>
      );

    case 'rich_text':
      return (
        <div
          className="prose prose-sm max-w-none text-zinc-700"
          dangerouslySetInnerHTML={{ __html: String(value) }}
        />
      );

    case 'image':
      return (
        <div>
          <img
            src={String(value)}
            alt={field.label}
            className="w-full rounded-xl object-cover"
            loading="lazy"
          />
        </div>
      );

    case 'color':
      return (
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-full border border-zinc-200 flex-shrink-0"
            style={{ backgroundColor: String(value) }}
          />
          <span className="text-sm text-zinc-500 font-mono">{String(value)}</span>
        </div>
      );

    case 'number':
      return (
        <div className="text-sm text-zinc-700">
          <span className="font-medium text-zinc-500">{field.label}: </span>
          {String(value)}
        </div>
      );

    case 'date':
    case 'datetime': {
      const d = new Date(String(value));
      const formatted = isNaN(d.getTime())
        ? String(value)
        : field.type === 'date'
          ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
          : d.toLocaleString('en-GB');
      return (
        <div className="text-sm text-zinc-700">
          <span className="font-medium text-zinc-500">{field.label}: </span>
          {formatted}
        </div>
      );
    }

    case 'array': {
      const items = Array.isArray(value) ? value : [];
      if (items.length === 0) return null;
      return (
        <div className="space-y-1">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{field.label}</p>
          <ul className="list-disc list-inside space-y-0.5 text-sm text-zinc-700">
            {items.map((item, i) => (
              <li key={i}>{String(item)}</li>
            ))}
          </ul>
        </div>
      );
    }

    case 'relation': {
      if (Array.isArray(value)) {
        const entries = value.filter(
          (v): v is { id: string; title: string; slug?: string | null } =>
            typeof v === 'object' && v !== null && 'id' in v,
        );
        if (entries.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-2">
            {entries.map((e) => (
              <span
                key={e.id}
                className="inline-flex items-center px-3 py-1 rounded-full bg-zinc-100 text-xs font-medium text-zinc-700"
              >
                {e.title}
              </span>
            ))}
          </div>
        );
      }

      if (typeof value === 'object' && value !== null && 'id' in value) {
        const e = value as { id: string; title: string; slug?: string | null };
        return <span className="text-sm font-medium text-zinc-900">{e.title}</span>;
      }

      return null;
    }

    default:
      return <div className="text-sm text-zinc-700">{String(value)}</div>;
  }
}

export function CollectionEntryPage() {
  const { typeSlug, slug } = useParams<{ typeSlug: string; slug: string }>();
  const navigate = useNavigate();

  const { data: result, isLoading, error } = useCollectionEntry(typeSlug, slug);

  useEffect(() => {
    if (!result) return;
    getTracker()?.track('article_read', {
      entryId: result.entry.id,
      entrySlug: result.entry.slug,
      collectionSlug: typeSlug ?? '',
      title: result.entry.title ?? '',
    });
  }, [result?.entry.id]);

  const is404 = error && (error as Error & { status?: number }).status === 404;

  if (is404) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4 px-4">
          <div>
            <h1 className="text-xl font-semibold text-zinc-800">Entry not found</h1>
            <p className="text-sm text-zinc-500 mt-1">
              The entry you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {result && (
        <>
          <PageSeo title={result.entry.title} />
          <StructuredData
            type="Article"
            headline={result.entry.title}
            url={typeof window !== 'undefined' ? window.location.href : ''}
            datePublished={result.entry.publishedAt ?? undefined}
            image={
              result.contentType.fieldsJson.find((f) => f.type === 'image') &&
              typeof result.entry.dataJson[
                result.contentType.fieldsJson.find((f) => f.type === 'image')!.key
              ] === 'string'
                ? String(result.entry.dataJson[
                    result.contentType.fieldsJson.find((f) => f.type === 'image')!.key
                  ])
                : undefined
            }
          />
        </>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <svg className="w-6 h-6 animate-spin text-zinc-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
          </svg>
        </div>
      ) : result ? (
        result.detailTemplate ? (
          <EntryContextProvider entry={result.entry} collection={result.contentType}>
            <TemplateBlockRenderer blocks={result.detailTemplate.blocks} />
          </EntryContextProvider>
        ) : (
        <article className="max-w-2xl mx-auto space-y-6 py-8 px-4">
          <button
            onClick={() => navigate(`/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug!)}`)}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {result.contentType.name}
          </button>

          <header className="space-y-2">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              {result.contentType.name}
            </p>
            <h1 className="text-2xl font-bold text-zinc-900 leading-snug">{result.entry.title}</h1>
            {result.entry.publishedAt && (
              <p className="text-sm text-zinc-400">{formatDate(result.entry.publishedAt)}</p>
            )}
          </header>

          <div className="border-t border-zinc-100" />

          {result.contentType.fieldsJson.length > 0 ? (
            <div className="space-y-5">
              {result.contentType.fieldsJson.map((field) => {
                const value = result.entry.dataJson[field.key];
                if (value === undefined || value === null || value === '') return null;
                if (field.type === 'boolean' && !value) return null;
                if (field.type === 'array' && Array.isArray(value) && value.length === 0) return null;

                if (field.type === 'rich_text' || field.type === 'image' || field.type === 'boolean') {
                  return <div key={field.key}><FieldValue field={field} value={value} /></div>;
                }

                return (
                  <div key={field.key} className="space-y-1">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                      {field.label}
                    </p>
                    <FieldValue field={field} value={value} />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 text-center py-8">No content fields defined.</p>
          )}
        </article>
        )
      ) : null}
    </SiteLayout>
  );
}
